import React, { useState, useRef, useEffect } from "react";
import "./style.scss";
import { Link, useLocation } from "react-router-dom";
import { scrollToTop, marketplace_banner } from "src/shared/utils/appConstant";
import { pageRoutes } from "src/shared/utils/pageRoutes.constant";
import { marketplaceProducts } from "src/shared/utils/string";
import Button from "src/components/button";
import Footer from "src/components/footer";
import { ProductCard } from "./Components/ProductCard";

export const Marketplace: React.FC = () => {
  const [viewMode, setViewMode] = useState<"deck" | "grid">("deck");
  const [activeCardId, setActiveCardId] = useState<string>("knoxhr");
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const cardsSectionRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const handleCardMouseEnter = (productId: string) => {
    setActiveCardId(productId);
  };

  const handleCardClick = (productId: string) => {
    setActiveCardId(productId);
  };

  // Check viewport width for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle hash change from navbar dropdown links
  useEffect(() => {
    if (location.hash) {
      const hashId = location.hash.replace("#", "").toLowerCase();
      const matched = marketplaceProducts.find((p) => p.id === hashId);
      if (matched) {
        setActiveCardId(matched.id);
        setTimeout(() => {
          const el =
            document.getElementById(`deck-card-${matched.id}`) ||
            document.getElementById(`grid-card-${matched.id}`) ||
            document.getElementById(`card-${matched.id}`);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          } else if (cardsSectionRef.current) {
            cardsSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 150);
      }
    }
  }, [location.hash]);

  return (
    <div className="marketplace-page">
      {/* 1. Hero Section matching site-wide image-container */}
      <div
        className="marketplace-hero solutions-image-container image-container position-rel"
        style={{ backgroundImage: `url(${marketplace_banner})` }}
      >
        <div className="text-container hero-text-container">
          <h1 className="heading hero-heading drop-in">Marketplace</h1>
          <p className="heading-desc display-none drop-in-2">
            Built from ideas. Driven by innovation. Designed for what’s next. Explore thoughtfully crafted products that challenge the ordinary, solve real-world problems, and turn bold possibilities into meaningful solutions.
          </p>
        </div>

        <div className="breadcrumb position-abs drop-in-2">
          <Link to={pageRoutes.Home} onClick={scrollToTop}>
            Home
          </Link>
          <span className="breadcrumb-separator">/</span>
          <p className="fw-light">
            <span>Marketplace</span>
          </p>
        </div>
        <div className="red-bg-box"></div>
      </div>

      {/* 2. Main Content: Crafting Ideas Into Solutions */}
      <section className="crafting-section" ref={cardsSectionRef}>
        <div className="crafting-header">
          <div className="crafting-left">
            <div className="crafting-title-wrap">
              <div className="title-grey-box" aria-hidden="true" />
              <h2 className="outline-heading">
                Crafting Ideas Into{' '}
                <span className="crafting-outline-row">
                  Solutions
                  <span className="mobile-dots" aria-hidden="true">
                    <span className="dot dot-red" />
                    <span className="dot dot-blue" />
                    <span className="dot dot-red" />
                  </span>
                </span>
              </h2>
            </div>
          </div>

          <div className="crafting-right">
            <div className="crafting-subtitle-box">
              <p>
                A collection of ideas transformed into solutions, thoughtfully built to
                solve challenges, simplify experiences, and create what's next.
              </p>
            </div>

            {/* View Mode Toggle (Deck vs Grid) */}
            <div className="view-mode-toggle">
              {/* Deck View Icon */}
              <button
                type="button"
                className={`toggle-btn ${viewMode === "deck" ? "active" : ""}`}
                onClick={() => setViewMode("deck")}
                title="Deck View"
                aria-label="Deck View"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <rect x="5" y="3" width="14" height="18" rx="3" fill={viewMode === "deck" ? "currentColor" : "none"} />
                  <path d="M9 3V21" stroke={viewMode === "deck" ? "#1c355e" : "currentColor"} />
                </svg>
              </button>

              {/* Grid View Icon */}
              <button
                type="button"
                className={`toggle-btn ${viewMode === "grid" ? "active" : ""}`}
                onClick={() => setViewMode("grid")}
                title="Grid View"
                aria-label="Grid View"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <rect x="3" y="3" width="7" height="7" rx="1.5" fill={viewMode === "grid" ? "currentColor" : "none"} />
                  <rect x="14" y="3" width="7" height="7" rx="1.5" fill={viewMode === "grid" ? "currentColor" : "none"} />
                  <rect x="3" y="14" width="7" height="7" rx="1.5" fill={viewMode === "grid" ? "currentColor" : "none"} />
                  <rect x="14" y="14" width="7" height="7" rx="1.5" fill={viewMode === "grid" ? "currentColor" : "none"} />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* 3. Product Cards Container */}
        {isMobile ? (
          /* Mobile View: Vertical Stack of Full Cards */
          <div className="cards-mobile-container">
            {marketplaceProducts.map((product) => (
              <ProductCard
                key={product.id}
                id={`card-${product.id}`}
                product={product}
                className="mobile-card"
              />
            ))}
          </div>
        ) : viewMode === "deck" ? (
          /* Deck View: Stacked Overlapping Cards with Pop-Out on Hover */
          <div className="cards-deck-container">
            <div className="deck-cards-wrapper">
              {marketplaceProducts.map((product, index) => {
                const isSelected = product.id === activeCardId;
                const zIndex = isSelected ? 50 : 35 - index;

                return (
                  <ProductCard
                    key={product.id}
                    id={`deck-card-${product.id}`}
                    product={product}
                    className={`deck-card deck-card-${index} ${
                      isSelected ? "active-card" : "stacked-card"
                    }`}
                    style={{
                      zIndex,
                    }}
                    onMouseEnter={() => handleCardMouseEnter(product.id)}
                    onClick={() => handleCardClick(product.id)}
                  />
                );
              })}
            </div>
          </div>
        ) : (
          /* Grid View: 4 cards top row, 3 cards bottom row with organic angles and pop-out on hover */
          <div className="cards-grid-container">
            {/* Top Row: 4 Cards */}
            <div className="grid-row top-row">
              {marketplaceProducts.slice(0, 4).map((product, idx) => (
                <ProductCard
                  key={product.id}
                  id={`grid-card-${product.id}`}
                  product={product}
                  className={`marketplace-grid-card grid-card-tilt-${idx + 1}`}
                />
              ))}
            </div>

            {/* Bottom Row: 3 Cards Centered */}
            <div className="grid-row bottom-row">
              {marketplaceProducts.slice(4, 7).map((product, idx) => (
                <ProductCard
                  key={product.id}
                  id={`grid-card-${product.id}`}
                  product={product}
                  className={`marketplace-grid-card grid-card-tilt-${idx + 5}`}
                />
              ))}
            </div>
          </div>
        )}
      </section>

      {/* 4. Get To Know Us Better Section - Reusing canonical Button component */}
      <section className="marketplace-know-us-better about-kub">
        <div className="kub-header">
          <h2 className="know-us-title">Get To Know Us Better</h2>
        </div>
        <div>
          <Link to={pageRoutes.Leadership} onClick={scrollToTop}>
            <Button>Leadership</Button>
          </Link>
        </div>
      </section>

      {/* 5. Footer */}
      <Footer />
    </div>
  );
};

export default Marketplace;
